import { useEffect, useRef, useState } from "react";

/**
 * Drag-to-reorder for a list, with native HTML5 drag and drop.
 *
 * Returns the list in its live (possibly mid-drag) order, props for each
 * row's drag handle and for the row itself (the drop target). Events stop
 * here, so a list nested in another (chapters in modules) drags on its own.
 * `onCommit(ids)` runs once when a drag ends with a changed order.
 */
export default function useDragReorder(items, onCommit) {
  const [order, setOrderState] = useState(items);
  const orderRef = useRef(items);
  const dragging = useRef(null);
  const changed = useRef(false);

  const setOrder = (next) => {
    orderRef.current = next;
    setOrderState(next);
  };

  useEffect(() => {
    orderRef.current = items;
    setOrderState(items);
  }, [items]);

  const handleProps = (id) => ({
    draggable: true,
    onDragStart: (event) => {
      event.stopPropagation();
      dragging.current = id;
      changed.current = false;
      event.dataTransfer.effectAllowed = "move";
      // Firefox needs data set to start a drag.
      event.dataTransfer.setData("text/plain", String(id));
    },
    onDragEnd: (event) => {
      event.stopPropagation();
      const wasChanged = changed.current;
      dragging.current = null;
      changed.current = false;
      if (wasChanged) onCommit(orderRef.current.map((item) => item.id));
    },
  });

  const rowProps = (id) => ({
    onDragOver: (event) => {
      if (dragging.current === null) return;
      event.preventDefault();
      event.stopPropagation();
      if (dragging.current === id) return;
      const current = orderRef.current;
      const from = current.findIndex((item) => item.id === dragging.current);
      const to = current.findIndex((item) => item.id === id);
      if (from < 0 || to < 0 || from === to) return;
      const next = [...current];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      changed.current = true;
      setOrder(next);
    },
    onDrop: (event) => {
      if (dragging.current === null) return;
      event.preventDefault();
      event.stopPropagation();
    },
  });

  /** Keyboard/menu alternative to dragging. */
  const move = (id, delta) => {
    const current = orderRef.current;
    const from = current.findIndex((item) => item.id === id);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= current.length) return;
    const next = [...current];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setOrder(next);
    onCommit(next.map((item) => item.id));
  };

  return { order, handleProps, rowProps, move };
}
